import { useRouter } from 'next/router';

type PageTransitionProps = {
  children: React.ReactNode;
};

// Visual fade wrapper for page navigations.
//
// History:
//
// v1 used `routeChangeStart`/`Complete` events plus a `displayChildren` state
//    buffer. Bug: if any route event was missed, the page got pinned to the
//    previous tree forever (white page with only a stale tooltip).
//
// v2 dropped the buffer but still triggered the fade off route events. Bug:
//    `routeChangeComplete` fires before `_app.tsx` re-renders with the new
//    `Component`, so the fade-in played against stale `children` — looked
//    like the loader fired twice.
//
// v3 used a `useEffect` on `router.pathname`. Bug: in some cases the effect
//    could fire more than once during one navigation, replaying the fade.
//
// v4 (current): no state, no effects, no event listeners. Just a `key` tied
//    to `router.pathname` on the wrapper div + CSS `animate-fadeIn`. React
//    remounts the wrapper whenever the underlying page route changes, the
//    keyframe plays once on mount, and there is no possible "fires twice"
//    failure mode because nothing imperative is running. Query-only updates
//    (e.g. dashboard tab switches via ?tabId=...) don't change pathname, so
//    they don't replay the fade.
export default function PageTransition({ children }: PageTransitionProps) {
  const router = useRouter();

  return (
    <div
      key={router.pathname}
      className="animate-fadeIn"
      style={{ minHeight: '100vh' }}
    >
      {children}
    </div>
  );
}
